import type { PullRequestAction, PullRequestMergeMethod } from "@cmmd-forge/contracts";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPopup,
  DialogTitle,
} from "~/components/ui/dialog";
import { MERGE_METHOD_LABELS } from "./PullRequestActionsMenu";

export function PullRequestActionConfirmation(props: {
  readonly action: PullRequestAction | null;
  readonly mergeMethod: PullRequestMergeMethod;
  readonly pending: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}) {
  const copy = confirmationCopy(props.action, props.mergeMethod);
  return (
    <Dialog
      open={props.action !== null}
      onOpenChange={(open) => {
        if (!open) props.onCancel();
      }}
    >
      <DialogPopup showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
          <DialogDescription>{copy.description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="outline" disabled={props.pending} onClick={props.onCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            variant={props.action === "close" ? "destructive" : "default"}
            disabled={props.pending}
            onClick={props.onConfirm}
          >
            {props.pending ? "Working…" : copy.confirmLabel}
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}

export function requiresPullRequestActionConfirmation(action: PullRequestAction): boolean {
  return action === "merge" || action === "enable-auto-merge" || action === "close";
}

function confirmationCopy(
  action: PullRequestAction | null,
  mergeMethod: PullRequestMergeMethod,
): { readonly title: string; readonly description: string; readonly confirmLabel: string } {
  if (action === "close") {
    return {
      title: "Close pull request?",
      description: "This closes the pull request without merging its changes.",
      confirmLabel: "Close pull request",
    };
  }
  if (action === "enable-auto-merge") {
    return {
      title: "Enable auto-merge?",
      description: `Command will ${MERGE_METHOD_LABELS[mergeMethod].toLowerCase()} this pull request automatically after its requirements pass.`,
      confirmLabel: "Enable auto-merge",
    };
  }
  return {
    title: `${MERGE_METHOD_LABELS[mergeMethod]} pull request?`,
    description: "This applies the selected merge method to the pull request through Command.",
    confirmLabel: MERGE_METHOD_LABELS[mergeMethod],
  };
}
